import { sectionRenderer } from '@theme/section-renderer';
import { Component } from '@theme/component';
import { FilterUpdateEvent, ThemeEvents } from '@theme/events';
import { debounce, formatMoney, startViewTransition } from '@theme/utilities';

/**
 * Search query parameter.
 * @type {string}
 */
const SEARCH_QUERY = 'q';

/** localStorage key prefix for saved filter params (per pathname). Only used on localhost. */
const FACETS_STORAGE_KEY_PREFIX = 'facets-filters-';

/**
 * @param {string} pathname
 * @returns {string}
 */
function getFacetsStorageKey(pathname) {
  return FACETS_STORAGE_KEY_PREFIX + pathname;
}

/**
 * Persist current filter params to localStorage when on localhost.
 * @param {string} searchParamsString - e.g. from URLSearchParams.toString()
 * @param {string} [pathname] - default current pathname
 */
function saveFacetsToLocalStorage(searchParamsString, pathname = window.location.pathname) {
  if (window.location.hostname !== 'localhost' || !searchParamsString) return;
  try {
    localStorage.setItem(getFacetsStorageKey(pathname), searchParamsString);
  } catch (e) {
    // ignore quota / private mode
  }
}

export const GRAPE_COLOUR_PARAM = 'filter.p.m.custom.grape_colour';

const GRAPE_COLOUR_GROUPS = [
  ['Red', 'Rouge'],
  ['White', 'Blanc'],
  ['Rose', 'Rosé'],
];

/**
 * @param {string} value
 * @returns {string}
 */
function normalizeGrapeColourValue(value) {
  return String(value)
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/\u0300-\u036f/g, '');
}

/**
 * Ensures grape_colour URL params include every OR synonym (Red+Rouge, White+Blanc, etc.).
 * @param {URLSearchParams} params
 */
export function expandGrapeColourInSearchParams(params) {
  const vals = params.getAll(GRAPE_COLOUR_PARAM);
  if (!vals.length) return;

  const toAppend = [];
  for (const grp of GRAPE_COLOUR_GROUPS) {
    const grpN = grp.map(normalizeGrapeColourValue);
    const touched = vals.some((v) => grpN.includes(normalizeGrapeColourValue(v)));
    if (!touched) continue;
    for (const spell of grp) {
      const sn = normalizeGrapeColourValue(spell);
      const have = vals.some((v) => normalizeGrapeColourValue(v) === sn);
      if (!have) toAppend.push(spell);
    }
  }
  if (!toAppend.length) return;

  toAppend.forEach((v) => params.append(GRAPE_COLOUR_PARAM, v));
}

/**
 * Sync merged grape-colour checkboxes so FormData includes every OR value (Red + Rouge, etc.).
 * @param {HTMLFormElement | null | undefined} form
 */
function syncGrapeColourMergedInputs(form) {
  if (!(form instanceof HTMLFormElement)) return;
  form.querySelectorAll('.grape-colour-merged').forEach((wrap) => {
    const inputs = [
      ...wrap.querySelectorAll(`input[type="checkbox"][name="${GRAPE_COLOUR_PARAM}"]`),
    ].filter((el) => el instanceof HTMLInputElement);
    if (inputs.length < 2) return;
    const leader = inputs[0];
    if (!leader) return;
    const state = leader.checked;
    for (let i = 1; i < inputs.length; i++) {
      const follower = inputs[i];
      if (follower) follower.checked = state;
    }
  });
}

/**
 * Paginated grids only expose the current page of cards to client-side facets (region/size/producer).
 * Counting `.product-grid__item` nodes would falsely show page size (e.g. 24) instead of the full result set.
 *
 * {@link snippets/product-grid.liquid} (`data-last-page`) tracks total pages after server pagination.
 * @returns {number}
 */
function mainCollectionPaginationPageCount() {
  const grid = document.querySelector('results-list ul.product-grid[data-last-page]');
  if (!grid) return 1;
  const n = Number.parseInt(grid.getAttribute('data-last-page'), 10);
  return Number.isFinite(n) && n > 0 ? n : 1;
}

/**
 * Updates facets bar product totals after client-side filtering. Hides counts when pagination is active.
 * @param {number} visibleCount
 */
function updateFacetClientFilteredProductCount(visibleCount) {
  const hideCount = mainCollectionPaginationPageCount() > 1;
  const wrappers = document.querySelectorAll('.products-count-wrapper');
  if (hideCount) {
    wrappers.forEach((wrapper) => {
      if (wrapper instanceof HTMLElement) wrapper.hidden = true;
    });
    return;
  }

  wrappers.forEach((wrapper) => {
    if (!(wrapper instanceof HTMLElement)) return;
    wrapper.hidden = false;
    const span = wrapper.querySelector(':scope > span');
    if (!(span instanceof HTMLElement)) return;
    const text = span.textContent;
    if (!text) return;
    const match = text.match(/(\d+)/);
    if (match) span.textContent = text.replace(/\d+/, String(visibleCount));
  });

  document.querySelectorAll('[title*="product_count"]').forEach((element) => {
    if (!(element instanceof HTMLElement)) return;
    if (element.closest('.products-count-wrapper')) return;
    if (!element.textContent) return;
    const match = element.textContent.match(/(\d+)/);
    if (match) element.textContent = element.textContent.replace(/\d+/, String(visibleCount));
  });
}

/**
 * Restore saved filter params from localStorage when on localhost and current URL has no filter params.
 * @returns {string | null} saved search string (without '?') or null
 */
function getSavedFacetsForRestore() {
  if (window.location.hostname !== 'localhost') return null;
  try {
    const key = getFacetsStorageKey(window.location.pathname);
    const saved = localStorage.getItem(key);
    if (!saved) return null;
    const current = new URLSearchParams(window.location.search);
    const hasFilterParams = [...current.keys()].some((k) => k.startsWith('filter.') || k === 'q' || k === 'sort_by');
    return hasFilterParams ? null : saved;
  } catch (e) {
    return null;
  }
}

/**
 * Sort product grid by displayed price when sort_by is price-descending or price-ascending.
 * Call after section render (e.g. filter change or pagination) so order matches displayed prices.
 * @param {Element} sectionEl - Section element containing ul.product-grid
 * @param {string} [sortByOverride] - Optional sort_by value (e.g. from requested URL) so sort works before location updates
 */
export function sortProductGridByDisplayPrice(sectionEl, sortByOverride) {
  if (!sectionEl) return;
  const grid = sectionEl.querySelector('ul.product-grid');
  if (!grid) return;
  const sortBy =
    sortByOverride != null && sortByOverride !== ''
      ? sortByOverride
      : (new URL(window.location.href).searchParams.get('sort_by') || '');
  const isPriceDesc = sortBy === 'price-descending' || sortBy === 'price-desc';
  const isPriceAsc = sortBy === 'price-ascending' || sortBy === 'price-asc';
  if (!isPriceDesc && !isPriceAsc) return;
  const productItems = [...grid.querySelectorAll('li.product-grid__item:not(.product-grid__hero-slot)')];
  if (productItems.length === 0) return;
  const heroSlot = grid.querySelector('li.product-grid__hero-slot');
  const desc = isPriceDesc;
  productItems.sort((a, b) => {
    const priceA = Number(a.getAttribute('data-sort-price')) || 0;
    const priceB = Number(b.getAttribute('data-sort-price')) || 0;
    return desc ? priceB - priceA : priceA - priceB;
  });
  while (grid.firstChild) grid.removeChild(grid.firstChild);
  if (heroSlot) {
    const beforeHero = productItems.slice(0, 12);
    const afterHero = productItems.slice(12);
    beforeHero.forEach((el) => grid.appendChild(el));
    grid.appendChild(heroSlot);
    afterHero.forEach((el) => grid.appendChild(el));
  } else {
    productItems.forEach((el) => grid.appendChild(el));
  }
}

/**
 * Handles the main facets form functionality
 *
 * @typedef {Object} FacetsFormRefs
 * @property {HTMLFormElement} facetsForm - The main facets form element
 * @property {HTMLElement | undefined} facetStatus - The facet status element
 *
 * @extends {Component<FacetsFormRefs>}
 */
class FacetsFormComponent extends Component {
  requiredRefs = ['facetsForm'];

  #boundOnSectionRendered = null;

  connectedCallback() {
    super.connectedCallback();
    // Sort product grid by display price on initial load when URL has price sort
    const run = () => this.#sortProductGridByDisplayPrice();
    requestAnimationFrame(run);
    queueMicrotask(run);
    setTimeout(run, 0);
    setTimeout(run, 50);
    // Re-sort when section is re-rendered (e.g. pagination) so price order stays correct
    this.#boundOnSectionRendered = (e) => {
      if (e.detail?.sectionId === this.sectionId) this.#sortProductGridByDisplayPrice();
    };
    document.addEventListener('section-rendered', this.#boundOnSectionRendered);
  }

  disconnectedCallback() {
    if (this.#boundOnSectionRendered) {
      document.removeEventListener('section-rendered', this.#boundOnSectionRendered);
    }
    super.disconnectedCallback();
  }

  /**
   * Creates URL parameters from form data
   * @param {FormData} [formData] - Optional form data to use instead of the main form
   * @returns {URLSearchParams} The processed URL parameters
   */
  createURLParameters(formData) {
    const form = this.refs.facetsForm;
    let fd;
    if (formData instanceof FormData) {
      fd = formData;
    } else {
      syncGrapeColourMergedInputs(form);
      fd = new FormData(form);
    }
    let newParameters = new URLSearchParams(/** @type any */ (fd));

    if (newParameters.get('filter.v.price.gte') === '') newParameters.delete('filter.v.price.gte');
    if (newParameters.get('filter.v.price.lte') === '') newParameters.delete('filter.v.price.lte');

    newParameters.delete('page');

    const searchQuery = this.#getSearchQuery();
    if (searchQuery) newParameters.set(SEARCH_QUERY, searchQuery);

    expandGrapeColourInSearchParams(newParameters);

    return newParameters;
  }

  /**
   * Gets the search query parameter from the current URL
   * @returns {string} The search query
   */
  #getSearchQuery() {
    const url = new URL(window.location.href);
    return url.searchParams.get(SEARCH_QUERY) ?? '';
  }

  get sectionId() {
    const id = this.getAttribute('section-id');
    if (!id) throw new Error('Section ID is required');
    return id;
  }

  /**
   * Updates the URL hash with current filter parameters
   */
  #updateURLHash() {
    const url = new URL(window.location.href);
    const urlParameters = this.createURLParameters();

    url.search = '';
    for (const [param, value] of urlParameters.entries()) {
      url.searchParams.append(param, value);
    }

    history.pushState({ urlParameters: urlParameters.toString() }, '', url.toString());
    saveFacetsToLocalStorage(urlParameters.toString());
  }

  /**
   * Updates filters and renders the section
   */
  updateFilters = () => {
    const params = this.createURLParameters();
    const regionIds = params.getAll('filter.region_id');
    if (regionIds.length) {
      console.log('[Facets form] updateFilters: filter.region_id=', regionIds, '→ updating URL and re-rendering section');
    }
    this.#updateURLHash();
    this.dispatchEvent(new FilterUpdateEvent(params));
    this.#updateSection();
  };

  /**
   * Capture IDs of open facet details so we can re-open them after section morph.
   * @returns {string[]}
   */
  #getOpenFacetDetailsIds() {
    return [...this.querySelectorAll('details[open][id]')].map((d) => d.id).filter(Boolean);
  }

  /**
   * Re-open facet details that were open before the section re-render.
   * Runs immediately and again after a short delay (in case morph replaced subtree or view transition delayed DOM update).
   * @param {string[]} openIds
   */
  #restoreOpenFacetDetails(openIds) {
    if (!openIds?.length) return;
    const apply = () => {
      for (const id of openIds) {
        const el = document.getElementById(id);
        if (el instanceof HTMLDetailsElement) el.open = true;
      }
    };
    apply();
    requestAnimationFrame(() => {
      apply();
      setTimeout(apply, 50);
    });
  }

  #sortProductGridByDisplayPrice() {
    sortProductGridByDisplayPrice(this.closest('.shopify-section'));
  }

  /**
   * Updates the section
   */
  #updateSection() {
    const openDetailsIds = this.#getOpenFacetDetailsIds();
    const inDialog = this.closest('dialog');
    const sectionEl = document.getElementById(this.sectionId);
    const hasProductGrid = sectionEl?.querySelector('.product-grid');
    // Skip view transition on producer (and other non-collection) sections to avoid
    // transition.types / duplicate view-transition-name errors and failed re-renders
    const useViewTransition = !inDialog && hasProductGrid;

    const afterRender = () => {
      this.#restoreOpenFacetDetails(openDetailsIds);
      this.#sortProductGridByDisplayPrice();
    };

    if (useViewTransition) {
      startViewTransition(() =>
        sectionRenderer.renderSection(this.sectionId).then(afterRender),
        ['product-grid']
      );
    } else {
      sectionRenderer.renderSection(this.sectionId).then(afterRender);
    }
  }

  /**
   * Updates filters based on a provided URL
   * @param {string} url - The URL to update filters with
   */
  updateFiltersByURL(url) {
    history.pushState('', '', url);
    const u = new URL(url, window.location.origin);
    saveFacetsToLocalStorage(u.search.slice(1), u.pathname);
    this.dispatchEvent(new FilterUpdateEvent(this.createURLParameters()));
    this.#updateSection();
  }
}

if (!customElements.get('facets-form-component')) {
  customElements.define('facets-form-component', FacetsFormComponent);
}

/**
 * @typedef {Object} FacetInputsRefs
 * @property {HTMLInputElement[]} facetInputs - The facet input elements
 */

/**
 * Handles individual facet input functionality
 * @extends {Component<FacetInputsRefs>}
 */
class FacetInputsComponent extends Component {
  get sectionId() {
    const id = this.closest('.shopify-section')?.id;
    if (!id) throw new Error('FacetInputs component must be a child of a section');
    return id;
  }

  /**
   * Updates filters and the selected facet summary
   */
  updateFilters() {
    const filterType = this.getAttribute('data-filter-type') || '';
    // region-producer: full page navigation so Liquid sees request.url and can filter producer cards
    // (Section Rendering API may not pass query string to Liquid on section fetch)
    const isRegionProducerFilter = filterType === 'region-producer';
    if (isRegionProducerFilter) {
      const facetsForm = this.closest('facets-form-component');
      if (facetsForm instanceof FacetsFormComponent) {
        const params = facetsForm.createURLParameters();
        const url = new URL(window.location.href);
        url.search = '';
        for (const [param, value] of params.entries()) {
          url.searchParams.append(param, value);
        }
        url.searchParams.delete('page');
        history.pushState({}, '', url.toString());
        this.#updateSelectedFacetSummary();
        document.dispatchEvent(new CustomEvent('producerRegionFilterUpdate'));
      }
      return;
    }
    // Region filter with Search & Discovery (server-side, filter.p.m.custom.region_sub_last_parent_id)
    const isRegionSDFilter = this.hasAttribute('data-filter-type') && filterType === 'region-sd';
    if (isRegionSDFilter) {
      this.#updateRegionFilterWithUrl();
      return;
    }

    // Region filter (client-side only, for producer/product grids using filter.region_id)
    const isRegionFilter = this.hasAttribute('data-filter-type') && filterType === 'region';
    if (isRegionFilter) {
      console.log('[Region filter] Client-side only (product grid)');
      this.#updateRegionFilter();
      return;
    }

    // Vintage filter: explicit URL + Section Rendering API (smooth UX, multi-select, no reset)
    const isVintageFilter = this.hasAttribute('data-filter-type') && filterType === 'vintage';
    if (isVintageFilter) {
      this.#updateVintageFilterWithUrl();
      return;
    }

    // Check if this is a size filter (client-side only)
    const isSizeFilter = this.hasAttribute('data-filter-type') && 
                        this.getAttribute('data-filter-type') === 'size';

    if (isSizeFilter) {
      this.#updateSizeFilter();
      return;
    }

    const facetsForm = this.closest('facets-form-component');

    if (!(facetsForm instanceof FacetsFormComponent)) return;

    syncGrapeColourMergedInputs(this.closest('form'));

    facetsForm.updateFilters();
    this.#updateSelectedFacetSummary();
  }

  /**
   * Vintage filter: build URL with filter params, pass explicitly to Section Rendering API (cache: false).
   * Section Rendering API may not pass query string to Liquid, so we restore checkbox state from URL
   * after the section re-renders.
   */
  #updateVintageFilterWithUrl() {
    const facetsForm = this.closest('facets-form-component');
    if (!(facetsForm instanceof FacetsFormComponent)) return;

    const sectionEl = document.getElementById(facetsForm.sectionId);
    const openDetailsIds = sectionEl
      ? [...sectionEl.querySelectorAll('details[open][id]')].map((d) => d.id).filter(Boolean)
      : [];

    const vintageInputs = this.querySelectorAll('input[name="filter.p.m.custom.vintage"]:checked');
    const selectedYears = Array.from(vintageInputs).map((input) => input.value);

    const url = new URL(window.location.href);
    url.searchParams.delete('filter.p.m.custom.vintage');
    url.searchParams.delete('page');
    selectedYears.forEach((year) => {
      url.searchParams.append('filter.p.m.custom.vintage', year);
    });
    url.hash = '';

    history.pushState({}, '', url.toString());
    this.#updateSelectedFacetSummary();

    const restoreOpen = () => {
      openDetailsIds.forEach((id) => {
        const el = document.getElementById(id);
        if (el instanceof HTMLDetailsElement) el.open = true;
      });
    };

    sectionRenderer.renderSection(facetsForm.sectionId, { url, cache: false }).then(() => {
      restoreOpen();
      requestAnimationFrame(() => {
        restoreOpen();
        setTimeout(restoreOpen, 50);
      });
      syncVintageCheckboxesFromUrl();
    });
  }

  /**
   * Region filter with Search & Discovery: build URL with filter.p.m.custom.region_sub_last_parent_id,
   * pass to Section Rendering API so Shopify filters products server-side.
   */
  #updateRegionFilterWithUrl() {
    const facetsForm = this.closest('facets-form-component');
    if (!(facetsForm instanceof FacetsFormComponent)) return;

    const sectionEl = document.getElementById(facetsForm.sectionId);
    const openDetailsIds = sectionEl
      ? [...sectionEl.querySelectorAll('details[open][id]')].map((d) => d.id).filter(Boolean)
      : [];

    const paramName = 'filter.p.m.custom.region_sub_last_parent_id';
    const regionInputs = this.querySelectorAll(`input[name="${paramName}"]:checked`);
    const selectedIds = Array.from(regionInputs).map((input) => input.value);

    const url = new URL(window.location.href);
    url.searchParams.delete(paramName);
    url.searchParams.delete('page');
    selectedIds.forEach((id) => {
      url.searchParams.append(paramName, id);
    });
    url.hash = '';

    history.pushState({}, '', url.toString());
    this.#updateSelectedFacetSummary();

    const restoreOpen = () => {
      openDetailsIds.forEach((id) => {
        const el = document.getElementById(id);
        if (el instanceof HTMLDetailsElement) el.open = true;
      });
    };

    sectionRenderer.renderSection(facetsForm.sectionId, { url, cache: false }).then(() => {
      restoreOpen();
      requestAnimationFrame(() => {
        restoreOpen();
        setTimeout(restoreOpen, 50);
      });
      syncRegionSDCheckboxesFromUrl();
      if (typeof window.repopulateRegionFilters === 'function') {
        window.repopulateRegionFilters().then?.(() => {
          syncRegionSDCheckboxesFromUrl();
          if (typeof window.updateRegionFilterPillsAndStatus === 'function') {
            window.updateRegionFilterPillsAndStatus();
          }
        });
      }
    });
  }

  /**
   * Updates region filter client-side only (no section re-render)
   */
  #updateRegionFilter() {
    const facetsForm = this.closest('facets-form-component');
    if (!(facetsForm instanceof FacetsFormComponent)) return;

    // Get selected region inputs
    const regionInputs = this.querySelectorAll('input[name="filter.region_id"]:checked');
    const selectedRegionIds = Array.from(regionInputs).map(input => input.value);

    // Update URL parameters without re-rendering section
    const urlParameters = facetsForm.createURLParameters();
    const url = new URL(window.location.href);
    
    // Clear existing region_id parameters
    url.searchParams.delete('filter.region_id');
    url.searchParams.delete('page'); // Reset to page 1 when filters change (correct UX)
    
    // Add selected region IDs
    selectedRegionIds.forEach(regionId => {
      url.searchParams.append('filter.region_id', regionId);
    });

    // Update URL without triggering section re-render
    history.pushState({ urlParameters: urlParameters.toString() }, '', url.toString());
    
    // Update filter status using the standard method
    this.#updateSelectedFacetSummary();
    
    // Trigger custom event for region filter handler to filter products
    document.dispatchEvent(new CustomEvent('regionFilterUpdate', { 
      detail: { regionIds: selectedRegionIds }
    }));
  }

  /**
   * Updates producer filter client-side only (no section re-render)
   */
  #updateProducerFilter() {
    const facetsForm = this.closest('facets-form-component');
    if (!(facetsForm instanceof FacetsFormComponent)) return;

    const producerInputs = this.querySelectorAll('input[name="filter.producer_id"]:checked');
    const selectedProducerIds = Array.from(producerInputs).map(input => input.value);

    const urlParameters = facetsForm.createURLParameters();
    const url = new URL(window.location.href);
    url.searchParams.delete('filter.producer_id');
    selectedProducerIds.forEach(producerId => {
      url.searchParams.append('filter.producer_id', producerId);
    });

    history.pushState({ urlParameters: urlParameters.toString() }, '', url.toString());
    this.#updateSelectedFacetSummary();
    document.dispatchEvent(new CustomEvent('producerFilterUpdate', { 
      detail: { producerIds: selectedProducerIds }
    }));
  }

  /**
   * Updates size filter client-side only (no section re-render)
   */
  #updateSizeFilter() {
    const facetsForm = this.closest('facets-form-component');
    if (!(facetsForm instanceof FacetsFormComponent)) return;

    const sizeInputs = this.querySelectorAll('input[name="filter.size"]:checked');
    const selectedSizes = Array.from(sizeInputs).map(input => input.value);

    const urlParameters = facetsForm.createURLParameters();
    const url = new URL(window.location.href);
    url.searchParams.delete('filter.size');
    url.searchParams.delete('page'); // Reset to page 1 when filters change (correct UX)
    selectedSizes.forEach(size => {
      url.searchParams.append('filter.size', size);
    });

    history.pushState({ urlParameters: urlParameters.toString() }, '', url.toString());
    this.#updateSelectedFacetSummary();
    document.dispatchEvent(new CustomEvent('sizeFilterUpdate', { 
      detail: { sizes: selectedSizes }
    }));
  }

  /**
   * Handles keydown events for the facets form
   * @param {KeyboardEvent} event - The keydown event
   */
  handleKeyDown(event) {
    if (!(event.target instanceof HTMLElement)) return;
    const closestInput = event.target.querySelector('input');

    if (!(closestInput instanceof HTMLInputElement)) return;

    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      closestInput.checked = !closestInput.checked;
      this.updateFilters();
    }
  }

  /**
   * Handles mouseover events on facet labels
   * @param {MouseEvent} event - The mouseover event
   */
  prefetchPage = debounce((event) => {
    if (!(event.target instanceof HTMLElement)) return;

    const form = this.closest('form');
    if (!form) return;

    syncGrapeColourMergedInputs(form);
    const formData = new FormData(form);
    const inputElement = event.target.querySelector('input');

    if (!(inputElement instanceof HTMLInputElement)) return;

    if (!inputElement.checked) formData.append(inputElement.name, inputElement.value);

    const facetsForm = this.closest('facets-form-component');
    if (!(facetsForm instanceof FacetsFormComponent)) return;

    const urlParameters = facetsForm.createURLParameters(formData);

    const url = new URL(window.location.pathname, window.location.origin);

    for (const [key, value] of urlParameters) url.searchParams.append(key, value);

    if (inputElement.checked) url.searchParams.delete(inputElement.name, inputElement.value);

    sectionRenderer.getSectionHTML(this.sectionId, true, url);
  }, 200);

  cancelPrefetchPage = () => this.prefetchPage.cancel();

  /**
   * Updates the selected facet summary
   */
  #updateSelectedFacetSummary() {
    if (!this.refs.facetInputs) return;

    const checkedInputElements = this.refs.facetInputs.filter((input) => input.checked);
    const details = this.closest('details');
    const statusComponent = details?.querySelector('facet-status-component');

    if (!(statusComponent instanceof FacetStatusComponent)) return;

    statusComponent.updateListSummary(checkedInputElements);
  }
}

if (!customElements.get('facet-inputs-component')) {
  customElements.define('facet-inputs-component', FacetInputsComponent);
}

/**
 * @typedef {Object} PriceFacetRefs
 * @property {HTMLInputElement} minInput - The minimum price input
 * @property {HTMLInputElement} maxInput - The maximum price input
 */

/**
 * Handles price facet functionality
 * @extends {Component<PriceFacetRefs>}
 */
class PriceFacetComponent extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('keydown', this.#onKeyDown);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keydown', this.#onKeyDown);
  }

  /**
   * Handles keydown events to restrict input to valid characters
   * @param {KeyboardEvent} event - The keydown event
   */
  #onKeyDown = (event) => {
    if (event.metaKey) return;

    const pattern = /[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;
    if (!event.key.match(pattern)) event.preventDefault();
  };

  /**
   * Updates price filter and results
   */
  updatePriceFilterAndResults() {
    const { minInput, maxInput } = this.refs;

    this.#adjustToValidValues(minInput);
    this.#adjustToValidValues(maxInput);

    const facetsForm = this.closest('facets-form-component');
    if (!(facetsForm instanceof FacetsFormComponent)) return;

    facetsForm.updateFilters();
    this.#setMinAndMaxValues();
    this.#updateSummary();
  }

  /**
   * Adjusts input values to be within valid range
   * @param {HTMLInputElement} input - The input element to adjust
   */
  #adjustToValidValues(input) {
    if (input.value.trim() === '') return;

    const value = Number(input.value);
    const min = Number(formatMoney(input.getAttribute('data-min') ?? ''));
    const max = Number(formatMoney(input.getAttribute('data-max') ?? ''));

    if (value < min) input.value = min.toString();
    if (value > max) input.value = max.toString();
  }

  /**
   * Sets min and max values for the inputs
   */
  #setMinAndMaxValues() {
    const { minInput, maxInput } = this.refs;

    if (maxInput.value) minInput.setAttribute('data-max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('data-min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('data-min', '0');
    if (maxInput.value === '') minInput.setAttribute('data-max', maxInput.getAttribute('data-max') ?? '');
  }

  /**
   * Updates the price summary
   */
  #updateSummary() {
    const { minInput, maxInput } = this.refs;
    const details = this.closest('details');
    const statusComponent = details?.querySelector('facet-status-component');

    if (!(statusComponent instanceof FacetStatusComponent)) return;

    statusComponent?.updatePriceSummary(minInput, maxInput);
  }
}

if (!customElements.get('price-facet-component')) {
  customElements.define('price-facet-component', PriceFacetComponent);
}

/**
 * Handles clearing of facet filters
 * @extends {Component}
 */
class FacetClearComponent extends Component {
  requiredRefs = ['clearButton'];

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('keyup', this.#handleKeyUp);
    document.addEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  /**
   * Clears the filter
   * @param {Event} event - The click event
   */
  clearFilter(event) {
    if (!(event.target instanceof HTMLElement)) return;

    if (event instanceof KeyboardEvent) {
      if (event.key !== 'Enter' && event.key !== ' ') {
        return;
      }
      event.preventDefault();
    }

    const container = event.target.closest('facet-inputs-component, price-facet-component');
    const isRegionFilter = container?.hasAttribute('data-filter-type') && 
                          container.getAttribute('data-filter-type') === 'region';
    const isRegionSDFilter = container?.hasAttribute('data-filter-type') && 
                             container.getAttribute('data-filter-type') === 'region-sd';
    const isProducerFilter = container?.hasAttribute('data-filter-type') && 
                             container.getAttribute('data-filter-type') === 'producer';
    const isVintageFilter = container?.hasAttribute('data-filter-type') && 
                           container.getAttribute('data-filter-type') === 'vintage';
    const isSizeFilter = container?.hasAttribute('data-filter-type') && 
                        container.getAttribute('data-filter-type') === 'size';

    container?.querySelectorAll('[type="checkbox"]:checked, input').forEach((input) => {
      if (input instanceof HTMLInputElement) {
        input.checked = false;
        input.value = '';
      }
    });

    const details = event.target.closest('details');
    const statusComponent = details?.querySelector('facet-status-component');

    if (!(statusComponent instanceof FacetStatusComponent)) return;

    statusComponent.clearSummary();

    const facetsForm = this.closest('facets-form-component');
    if (!(facetsForm instanceof FacetsFormComponent)) return;

    if ((isRegionFilter || isRegionSDFilter || isProducerFilter || isVintageFilter || isSizeFilter) && container instanceof FacetInputsComponent) {
      container.updateFilters();
    } else {
      facetsForm.updateFilters();
    }
  }

  /**
   * Handles keyup events
   * @param {KeyboardEvent} event - The keyup event
   */
  #handleKeyUp = (event) => {
    if (event.metaKey) return;
    if (event.key === 'Enter') this.clearFilter(event);
  };

  /**
   * Toggle clear button visibility when filters are applied. Happens before the
   * Section Rendering Request resolves.
   *
   * @param {FilterUpdateEvent} event
   */
  #handleFilterUpdate = (event) => {
    const { clearButton } = this.refs;
    if (clearButton instanceof Element) {
      clearButton.classList.toggle('facets__clear--active', event.shouldShowClearAll());
    }
  };
}

if (!customElements.get('facet-clear-component')) {
  customElements.define('facet-clear-component', FacetClearComponent);
}

/**
 * @typedef {Object} FacetRemoveComponentRefs
 * @property {HTMLInputElement | undefined} clearButton - The button to clear filters
 */

/**
 * Handles removal of individual facet filters
 * @extends {Component<FacetRemoveComponentRefs>}
 */
class FacetRemoveComponent extends Component {
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  /**
   * Removes the filter
   * @param {Object} data - The data object
   * @param {string} data.form - The form to remove the filter from
   * @param {Event} event - The click event
   */
  removeFilter({ form }, event) {
    if (event instanceof KeyboardEvent) {
      if (event.key !== 'Enter' && event.key !== ' ') {
        return;
      }
      event.preventDefault();
    }

    const url = this.dataset.url;
    if (!url) return;

    const facetsForm = form ? document.getElementById(form) : this.closest('facets-form-component');

    if (!(facetsForm instanceof FacetsFormComponent)) return;

    facetsForm.updateFiltersByURL(url);
  }

  /**
   * Toggle clear button visibility when filters are applied. Happens before the
   * Section Rendering Request resolves.
   *
   * @param {FilterUpdateEvent} event
   */
  #handleFilterUpdate = (event) => {
    const { clearButton } = this.refs;
    if (clearButton instanceof Element) {
      clearButton.classList.toggle('active', event.shouldShowClearAll());
    }
  };
}

if (!customElements.get('facet-remove-component')) {
  customElements.define('facet-remove-component', FacetRemoveComponent);
}

/**
 * Handles sorting filter functionality
 *
 * @typedef {Object} SortingFilterRefs
 * @property {HTMLDetailsElement} details - The details element
 * @property {HTMLElement} summary - The summary element
 * @property {HTMLElement} listbox - The listbox element
 *
 * @extends {Component}
 */
class SortingFilterComponent extends Component {
  requiredRefs = ['details', 'summary', 'listbox'];

  /**
   * Handles keyboard navigation in the sorting dropdown
   * @param {KeyboardEvent} event - The keyboard event
   */
  handleKeyDown = (event) => {
    const { listbox } = this.refs;
    if (!(listbox instanceof Element)) return;

    const options = Array.from(listbox.querySelectorAll('[role="option"]'));
    const currentFocused = options.find((option) => option instanceof HTMLElement && option.tabIndex === 0);
    let newFocusIndex = currentFocused ? options.indexOf(currentFocused) : 0;

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        newFocusIndex = Math.min(newFocusIndex + 1, options.length - 1);
        this.#moveFocus(options, newFocusIndex);
        break;

      case 'ArrowUp':
        event.preventDefault();
        newFocusIndex = Math.max(newFocusIndex - 1, 0);
        this.#moveFocus(options, newFocusIndex);
        break;

      case 'Enter':
      case ' ':
        if (event.target instanceof Element) {
          const targetOption = event.target.closest('[role="option"]');
          if (targetOption) {
            event.preventDefault();
            this.#selectOption(targetOption);
          }
        }
        break;

      case 'Escape':
        event.preventDefault();
        this.#closeDropdown();
        break;
    }
  };

  /**
   * Handles details toggle event
   */
  handleToggle = () => {
    const { details, summary, listbox } = this.refs;
    if (!(details instanceof HTMLDetailsElement) || !(summary instanceof HTMLElement)) return;

    const isOpen = details.open;
    summary.setAttribute('aria-expanded', isOpen.toString());

    if (isOpen && listbox instanceof Element) {
      // Move focus to selected option when dropdown opens
      const selectedOption = listbox.querySelector('[aria-selected="true"]');
      if (selectedOption instanceof HTMLElement) {
        selectedOption.focus();
      }
    }
  };

  /**
   * Moves focus between options
   * @param {Element[]} options - The option elements
   * @param {number} newIndex - The index of the option to focus
   */
  #moveFocus(options, newIndex) {
    // Remove tabindex from all options
    options.forEach((option) => {
      if (option instanceof HTMLElement) {
        option.tabIndex = -1;
      }
    });

    // Set tabindex and focus on new option
    const targetOption = options[newIndex];
    if (targetOption instanceof HTMLElement) {
      targetOption.tabIndex = 0;
      targetOption.focus();
    }
  }

  /**
   * Selects an option and triggers form submission
   * @param {Element} option - The option element to select
   */
  #selectOption(option) {
    const input = option.querySelector('input[type="radio"]');
    if (input instanceof HTMLInputElement && option instanceof HTMLElement) {
      // Update aria-selected states
      this.querySelectorAll('[role="option"]').forEach((opt) => {
        opt.setAttribute('aria-selected', 'false');
      });
      option.setAttribute('aria-selected', 'true');

      // Trigger click on the input to ensure normal form behavior
      input.click();

      // Close dropdown and return focus (handles tabIndex reset)
      this.#closeDropdown();
    }
  }

  /**
   * Closes the dropdown and returns focus to summary
   */
  #closeDropdown() {
    const { details, summary } = this.refs;
    if (details instanceof HTMLDetailsElement) {
      // Reset focus to match the actual selected option
      const options = this.querySelectorAll('[role="option"]');
      const selectedOption = this.querySelector('[aria-selected="true"]');

      options.forEach((opt) => {
        if (opt instanceof HTMLElement) {
          opt.tabIndex = -1;
        }
      });

      if (selectedOption instanceof HTMLElement) {
        selectedOption.tabIndex = 0;
      }

      details.open = false;
      if (summary instanceof HTMLElement) {
        summary.focus();
      }
    }
  }

  /**
   * Updates filter and sorting
   * @param {Event} event - The change event
   */
  updateFilterAndSorting(event) {
    const facetsForm =
      this.closest('facets-form-component') || this.closest('.shopify-section')?.querySelector('facets-form-component');

    if (!(facetsForm instanceof FacetsFormComponent)) return;
    const isMobile = window.innerWidth < 750;

    const shouldDisable = this.dataset.shouldUseSelectOnMobile === 'true';

    // Because we have a select element on mobile and a bunch of radio buttons on desktop,
    // we need to disable the input during "form-submission" to prevent duplicate entries.
    if (shouldDisable) {
      if (isMobile) {
        const inputs = this.querySelectorAll('input[name="sort_by"]');
        inputs.forEach((input) => {
          if (!(input instanceof HTMLInputElement)) return;
          input.disabled = true;
        });
      } else {
        const selectElement = this.querySelector('select[name="sort_by"]');
        if (!(selectElement instanceof HTMLSelectElement)) return;
        selectElement.disabled = true;
      }
    }

    facetsForm.updateFilters();
    this.updateFacetStatus(event);

    // Re-enable the input after the form-submission
    if (shouldDisable) {
      if (isMobile) {
        const inputs = this.querySelectorAll('input[name="sort_by"]');
        inputs.forEach((input) => {
          if (!(input instanceof HTMLInputElement)) return;
          input.disabled = false;
        });
      } else {
        const selectElement = this.querySelector('select[name="sort_by"]');
        if (!(selectElement instanceof HTMLSelectElement)) return;
        selectElement.disabled = false;
      }
    }

    // Close the details element when a value is selected
    const { details } = this.refs;
    if (!(details instanceof HTMLDetailsElement)) return;
    details.open = false;
  }

  /**
   * Updates the facet status
   * @param {Event} event - The change event
   */
  updateFacetStatus(event) {
    if (!(event.target instanceof HTMLSelectElement)) return;

    const details = this.querySelector('details');
    if (!details) return;

    const facetStatus = details.querySelector('facet-status-component');
    if (!(facetStatus instanceof FacetStatusComponent)) return;

    facetStatus.textContent =
      event.target.value !== details.dataset.defaultSortBy ? event.target.dataset.optionName ?? '' : '';
  }
}

if (!customElements.get('sorting-filter-component')) {
  customElements.define('sorting-filter-component', SortingFilterComponent);
}

/**
 * @typedef {Object} FacetStatusRefs
 * @property {HTMLElement} facetStatus - The facet status element
 */

/**
 * Handles facet status display
 * @extends {Component<FacetStatusRefs>}
 */
class FacetStatusComponent extends Component {
  /**
   * Updates the list summary
   * @param {HTMLInputElement[]} checkedInputElements - The checked input elements
   */
  updateListSummary(checkedInputElements) {
    const checkedInputElementsCount = checkedInputElements.length;

    this.getAttribute('facet-type') === 'swatches'
      ? this.#updateSwatchSummary(checkedInputElements, checkedInputElementsCount)
      : this.#updateBubbleSummary(checkedInputElements, checkedInputElementsCount);
  }

  /**
   * Updates the swatch summary
   * @param {HTMLInputElement[]} checkedInputElements - The checked input elements
   * @param {number} checkedInputElementsCount - The number of checked inputs
   */
  #updateSwatchSummary(checkedInputElements, checkedInputElementsCount) {
    const { facetStatus } = this.refs;
    facetStatus.classList.remove('bubble', 'facets__bubble');

    if (checkedInputElementsCount === 0) {
      facetStatus.innerHTML = '';
      return;
    }

    if (checkedInputElementsCount > 3) {
      facetStatus.innerHTML = checkedInputElementsCount.toString();
      facetStatus.classList.add('bubble', 'facets__bubble');
      return;
    }

    facetStatus.innerHTML = Array.from(checkedInputElements)
      .map((inputElement) => {
        const swatch = inputElement.parentElement?.querySelector('span.swatch');
        return swatch?.outerHTML ?? '';
      })
      .join('');
  }

  /**
   * Updates the bubble summary
   * @param {HTMLInputElement[]} checkedInputElements - The checked input elements
   * @param {number} checkedInputElementsCount - The number of checked inputs
   */
  #updateBubbleSummary(checkedInputElements, checkedInputElementsCount) {
    const { facetStatus } = this.refs;
    facetStatus.classList.remove('bubble', 'facets__bubble');

    if (checkedInputElementsCount === 0) {
      facetStatus.innerHTML = '';
      return;
    }

    if (checkedInputElementsCount === 1) {
      facetStatus.innerHTML = checkedInputElements[0]?.dataset.label ?? '';
      return;
    }

    facetStatus.innerHTML = checkedInputElementsCount.toString();
    facetStatus.classList.add('bubble', 'facets__bubble');
  }

  /**
   * Updates the price summary
   * @param {HTMLInputElement} minInput - The minimum price input
   * @param {HTMLInputElement} maxInput - The maximum price input
   */
  updatePriceSummary(minInput, maxInput) {
    const minInputValue = minInput.value;
    const maxInputValue = maxInput.value;
    const { facetStatus } = this.refs;

    if (!minInputValue && !maxInputValue) {
      facetStatus.innerHTML = '';
      return;
    }

    const minInputNum = this.#parseCents(minInputValue, '0');
    const maxInputNum = this.#parseCents(maxInputValue, facetStatus.dataset.rangeMax);
    facetStatus.innerHTML = `${this.#formatMoney(minInputNum)}–${this.#formatMoney(maxInputNum)}`;
  }

  /**
   * Parses a decimal number as cents
   * @param {string} value - The stringified decimal number to parse
   * @param {string} fallback - The fallback value in case `value` is invalid
   * @returns {number} The money value in cents
   */
  #parseCents(value, fallback = '0') {
    const parts = value ? value.trim().split(/[^0-9]/) : (parseInt(fallback, 10) / 100).toString();
    const [wholeStr, fractionStr, ...rest] = parts;
    if (typeof wholeStr !== 'string' || rest.length > 0) return parseInt(fallback, 10);

    const whole = parseInt(wholeStr, 10);
    let fraction = parseInt(fractionStr || '0', 10);

    // Use two most-significant digits, e.g. 1 -> 10, 12 -> 12, 123 -> 12.3, 1234 -> 12.34, etc
    fraction = fraction * Math.pow(10, 2 - fraction.toString().length);

    return whole * 100 + fraction;
  }

  /**
   * Formats money, replicated the implementation of the `money` liquid filters
   * @param {number} moneyValue - The money value
   * @returns {string} The formatted money value
   */
  #formatMoney(moneyValue) {
    if (!(this.refs.moneyFormat instanceof HTMLTemplateElement)) return '';

    const template = this.refs.moneyFormat.content.textContent || '{{amount}}';
    const currency = this.refs.facetStatus.dataset.currency || '';

    return template.replace(/{{\s*(\w+)\s*}}/g, (_, placeholder) => {
      if (typeof placeholder !== 'string') return '';
      if (placeholder === 'currency') return currency;

      let thousandsSeparator = ',';
      let decimalSeparator = '.';
      let precision = CURRENCY_DECIMALS[currency.toUpperCase()] ?? DEFAULT_CURRENCY_DECIMALS;

      if (placeholder === 'amount') {
        // Check first since it's the most common, use defaults.
      } else if (placeholder === 'amount_no_decimals') {
        precision = 0;
      } else if (placeholder === 'amount_with_comma_separator') {
        thousandsSeparator = '.';
        decimalSeparator = ',';
      } else if (placeholder === 'amount_no_decimals_with_comma_separator') {
        // Weirdly, this is correct. It uses amount_with_comma_separator's
        // behaviour but removes decimals, resulting in an unintuitive
        // output that can't possibly include commas, despite the name.
        thousandsSeparator = '.';
        precision = 0;
      } else if (placeholder === 'amount_no_decimals_with_space_separator') {
        thousandsSeparator = ' ';
        precision = 0;
      } else if (placeholder === 'amount_with_space_separator') {
        thousandsSeparator = ' ';
        decimalSeparator = ',';
      } else if (placeholder === 'amount_with_period_and_space_separator') {
        thousandsSeparator = ' ';
        decimalSeparator = '.';
      } else if (placeholder === 'amount_with_apostrophe_separator') {
        thousandsSeparator = "'";
        decimalSeparator = '.';
      }

      return this.#formatCents(moneyValue, thousandsSeparator, decimalSeparator, precision);
    });
  }

  /**
   * Formats money in cents
   * @param {number} moneyValue - The money value in cents (hundredths of one major currency unit)
   * @param {string} thousandsSeparator - The thousands separator
   * @param {string} decimalSeparator - The decimal separator
   * @param {number} precision - The precision
   * @returns {string} The formatted money value
   */
  #formatCents(moneyValue, thousandsSeparator, decimalSeparator, precision) {
    const roundedNumber = (moneyValue / 100).toFixed(precision);

    let [a, b] = roundedNumber.split('.');
    if (!a) a = '0';
    if (!b) b = '';

    // Split by groups of 3 digits
    a = a.replace(/\d(?=(\d\d\d)+(?!\d))/g, (digit) => digit + thousandsSeparator);

    return precision <= 0 ? a : a + decimalSeparator + b.padEnd(precision, '0');
  }

  /**
   * Clears the summary
   */
  clearSummary() {
    this.refs.facetStatus.innerHTML = '';
  }
}

if (!customElements.get('facet-status-component')) {
  customElements.define('facet-status-component', FacetStatusComponent);
}

/**
 * Default currency decimals used in most currenies
 * @constant {number}
 */
const DEFAULT_CURRENCY_DECIMALS = 2;

/**
 * Decimal precision for currencies that have a non-default precision
 * @type {Record<string, number>}
 */
const CURRENCY_DECIMALS = {
  BHD: 3,
  BIF: 0,
  BYR: 0,
  CLF: 4,
  CLP: 0,
  DJF: 0,
  GNF: 0,
  IQD: 3,
  ISK: 0,
  JOD: 3,
  JPY: 0,
  KMF: 0,
  KRW: 0,
  KWD: 3,
  LYD: 3,
  MRO: 5,
  OMR: 3,
  PYG: 0,
  RWF: 0,
  TND: 3,
  UGX: 0,
  UYI: 0,
  UYW: 4,
  VND: 0,
  VUV: 0,
  XAF: 0,
  XAG: 0,
  XAU: 0,
  XBA: 0,
  XBB: 0,
  XBC: 0,
  XBD: 0,
  XDR: 0,
  XOF: 0,
  XPD: 0,
  XPF: 0,
  XPT: 0,
  XSU: 0,
  XTS: 0,
  XUA: 0,
};

/**
 * Handles region filtering for products
 * This extends the FacetInputsComponent to filter products client-side
 * based on their data-region-id attribute
 */
class RegionFilterHandler {
  constructor() {
    this.observer = null;
    this.init();
  }

  init() {
    // Listen for region filter update events (custom event, no section re-render)
    document.addEventListener('regionFilterUpdate', this.handleRegionFilterUpdate.bind(this));
    
    // Listen for URL changes (e.g., when "Clear all" is clicked or browser back/forward)
    window.addEventListener('popstate', this.handleURLChange.bind(this));
    
    // Use MutationObserver to watch for section re-renders (from other filters)
    this.setupMutationObserver();
    
    // Apply initial filters on page load and sync with URL
    requestAnimationFrame(() => {
      this.syncRegionFiltersWithURL();
      this.applyRegionFilters();
      this.updateRegionFilterStatus();
    });
  }

  /**
   * Syncs region filter checkboxes with URL parameters.
   * Touches all collection region facets (data-filter-type="region"), e.g. desktop and drawer.
   */
  syncRegionFiltersWithURL() {
    const url = new URL(window.location.href);

    // Region-sd (Search & Discovery, collection page)
    const regionSDFacets = document.querySelectorAll('facet-inputs-component[data-filter-type="region-sd"]');
    const regionSDParam = 'filter.p.m.custom.region_sub_last_parent_id';
    const regionSDParams = url.searchParams.getAll(regionSDParam);
    regionSDFacets.forEach(facet => {
      facet.querySelectorAll(`input[name="${regionSDParam}"]`).forEach(input => {
        if (input instanceof HTMLInputElement) input.checked = false;
      });
      regionSDParams.forEach(regionId => {
        const input = facet.querySelector(`input[name="${regionSDParam}"][value="${regionId}"]`);
        if (input instanceof HTMLInputElement) input.checked = true;
      });
    });

    // Region (filter.region_id, client-side, e.g. producer)
    const collectionRegionFacets = document.querySelectorAll('facet-inputs-component[data-filter-type="region"]');
    const regionParams = url.searchParams.getAll('filter.region_id');
    collectionRegionFacets.forEach(facet => {
      facet.querySelectorAll('input[name="filter.region_id"]').forEach(input => {
        if (input instanceof HTMLInputElement) input.checked = false;
      });
      if (regionParams.length > 0) {
        regionParams.forEach(regionId => {
          const input = facet.querySelector(`input[name="filter.region_id"][value="${regionId}"]`);
          if (input instanceof HTMLInputElement) input.checked = true;
        });
      }
    });
  }

  /**
   * Handles URL changes (e.g., when "Clear all" is clicked)
   */
  handleURLChange() {
    // Sync checkboxes with URL, then apply filters
    this.syncRegionFiltersWithURL();
    this.applyRegionFilters();
    this.updateRegionFilterStatus();
  }

  /**
   * Sets up a MutationObserver to watch for section re-renders (collection page only).
   */
  setupMutationObserver() {
    const section = document.querySelector('results-list');
    if (!section) return;

    this.observer = new MutationObserver((mutations) => {
      let shouldReapply = false;
      mutations.forEach((mutation) => {
        if (mutation.addedNodes.length > 0) {
          // Check if product cards were added
          mutation.addedNodes.forEach((node) => {
            if (node instanceof HTMLElement) {
              if (node.classList.contains('product-grid__item') || 
                  node.querySelector('.product-grid__item')) {
                shouldReapply = true;
              }
            }
          });
        }
      });

      if (shouldReapply) {
        // Small delay to ensure DOM is fully updated
        // This handles section re-renders from other filters (not region filter)
        setTimeout(() => {
          // Sync with URL in case filters were cleared
          this.syncRegionFiltersWithURL();
          this.applyRegionFilters();
          this.updateRegionFilterStatus();
        }, 50);
      }
    });

    this.observer.observe(section, {
      childList: true,
      subtree: true
    });
  }

  /**
   * Handles region filter update events (custom event, no section re-render)
   * @param {CustomEvent} event
   */
  handleRegionFilterUpdate(event) {
    // Apply filters immediately when region filter changes
    this.applyRegionFilters();
    this.updateRegionFilterStatus();
  }


  /**
   * Gets selected region IDs from any collection region facet (desktop or drawer).
   * Supports both region (filter.region_id) and region-sd (filter.p.m.custom.region_sub_last_parent_id).
   * Falls back to URL when inputs don't exist yet (e.g. before region list is populated).
   * @returns {string[]}
   */
  getSelectedRegionIds() {
    const regionSDFacet = document.querySelector('facet-inputs-component[data-filter-type="region-sd"]');
    if (regionSDFacet) {
      const inputs = regionSDFacet.querySelectorAll('input[name="filter.p.m.custom.region_sub_last_parent_id"]:checked');
      const fromInputs = Array.from(inputs).map((input) => input.value);
      if (fromInputs.length > 0) return fromInputs;
      const url = new URL(window.location.href);
      return url.searchParams.getAll('filter.p.m.custom.region_sub_last_parent_id') || [];
    }
    const regionFacet = document.querySelector('facet-inputs-component[data-filter-type="region"]');
    if (regionFacet) {
      const inputs = regionFacet.querySelectorAll('input[name="filter.region_id"]:checked');
      return Array.from(inputs).map((input) => input.value);
    }
    return [];
  }

  /**
   * Applies region filters to product cards (collection page only).
   * Uses data-region-sub-last-parent-id (Top Level Regions) – values are sub_last_parent_id (e.g. 7, 8, 545).
   */
  applyRegionFilters() {
    const selectedRegionIds = this.getSelectedRegionIds();
    const section = document.querySelector('results-list');
    if (!section) return;

    const allProductCards = section.querySelectorAll('.product-grid__item');
    const productCardsWithRegionSubId = section.querySelectorAll('.product-grid__item[data-region-sub-last-parent-id]');
    let visibleCount = 0;

    // If no region filters are selected, show all products
    if (selectedRegionIds.length === 0) {
      allProductCards.forEach(card => {
        if (!(card instanceof HTMLElement)) return;
        
        if (card.style.display === 'none') {
          card.style.removeProperty('display');
        }
        
        const computedStyle = window.getComputedStyle(card);
        if (computedStyle.display !== 'none') {
          visibleCount++;
        }
      });
      this.updateProductCount(visibleCount);
      return;
    }

    // Filter products by data-region-sub-last-parent-id (Top Level Regions)
    productCardsWithRegionSubId.forEach(card => {
      if (!(card instanceof HTMLElement)) return;

      const regionSubId = card.dataset.regionSubLastParentId;

      if (regionSubId && selectedRegionIds.includes(regionSubId)) {
        card.style.display = '';
        visibleCount++;
      } else {
        const computedStyle = window.getComputedStyle(card);
        if (computedStyle.display === 'none') return;
        card.style.display = 'none';
      }
    });

    // Hide products without data-region-sub-last-parent-id when region filter is active
    allProductCards.forEach(card => {
      if (!(card instanceof HTMLElement)) return;
      if (!card.hasAttribute('data-region-sub-last-parent-id')) {
        const computedStyle = window.getComputedStyle(card);
        if (computedStyle.display === 'none') return;
        card.style.display = 'none';
      }
    });

    this.updateProductCount(visibleCount);
  }

  /**
   * Updates the region filter status display
   */
  updateRegionFilterStatus() {
    const selectedRegionIds = this.getSelectedRegionIds();
    // Find status elements by ID pattern or by being inside region filter components
    const statusElements = document.querySelectorAll('[id^="region-filter-status-"], [id^="top-level-regions-filter-status-"], facet-inputs-component[data-filter-type="region"] facet-status-component span[ref="facetStatus"], facet-inputs-component[data-filter-type="region-sd"] facet-status-component span[ref="facetStatus"]');
    
    statusElements.forEach(statusElement => {
      if (!(statusElement instanceof HTMLElement)) return;
      
      if (selectedRegionIds.length === 0) {
        statusElement.innerHTML = '';
        statusElement.classList.remove('bubble', 'facets__bubble');
        return;
      }

      // Get region names for display (from inputs or __regionIdToName when inputs don't exist yet)
      const regionIdToName = window.__regionIdToName || {};
      const regionNames = selectedRegionIds.map(regionId => {
        const regionInput = document.querySelector(`input[name="filter.region_id"][value="${regionId}"], input[name="filter.p.m.custom.region_sub_last_parent_id"][value="${regionId}"]`);
        if (regionInput) {
          return regionInput.getAttribute('data-label') || regionInput.closest('li')?.querySelector('label')?.textContent?.trim() || '';
        }
        return regionIdToName[regionId] || '';
      }).filter(name => name);

      if (selectedRegionIds.length === 1 && regionNames.length >= 1 && regionNames[0]) {
        statusElement.innerHTML = regionNames[0];
        statusElement.classList.remove('bubble', 'facets__bubble');
      } else {
        statusElement.innerHTML = selectedRegionIds.length.toString();
        statusElement.classList.add('bubble', 'facets__bubble');
      }
    });
  }

  /**
   * Updates the product count display
   * @param {number} count
   */
  updateProductCount(count) {
    updateFacetClientFilteredProductCount(count);
  }
}

/**
 * Handles producer filtering for products based on data-producer-id attribute
 */
class ProducerFilterHandler {
  constructor() {
    this.observer = null;
    this.init();
  }

  init() {
    document.addEventListener('producerFilterUpdate', this.handleProducerFilterUpdate.bind(this));
    window.addEventListener('popstate', this.handleURLChange.bind(this));
    this.setupMutationObserver();
    requestAnimationFrame(() => {
      this.syncProducerFiltersWithURL();
      this.applyProducerFilters();
      this.updateProducerFilterStatus();
    });
  }

  syncProducerFiltersWithURL() {
    const url = new URL(window.location.href);
    const producerParams = url.searchParams.getAll('filter.producer_id');
    const allProducerInputs = document.querySelectorAll('input[name="filter.producer_id"]');
    allProducerInputs.forEach(input => {
      if (input instanceof HTMLInputElement) input.checked = false;
    });
    if (producerParams.length > 0) {
      producerParams.forEach(producerId => {
        const input = document.querySelector(`input[name="filter.producer_id"][value="${producerId}"]`);
        if (input instanceof HTMLInputElement) input.checked = true;
      });
    }
  }

  handleURLChange() {
    this.syncProducerFiltersWithURL();
    this.applyProducerFilters();
    this.updateProducerFilterStatus();
  }

  setupMutationObserver() {
    const section = document.querySelector('results-list, [section-id]');
    if (!section) return;
    this.observer = new MutationObserver((mutations) => {
      let shouldReapply = false;
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node instanceof HTMLElement && (node.classList.contains('product-grid__item') || node.querySelector('.product-grid__item'))) {
            shouldReapply = true;
          }
        });
      });
      if (shouldReapply) {
        setTimeout(() => {
          this.syncProducerFiltersWithURL();
          this.applyProducerFilters();
          this.updateProducerFilterStatus();
        }, 50);
      }
    });
    this.observer.observe(section, { childList: true, subtree: true });
  }

  handleProducerFilterUpdate() {
    this.applyProducerFilters();
    this.updateProducerFilterStatus();
  }

  getSelectedProducerIds() {
    const inputs = document.querySelectorAll('input[name="filter.producer_id"]:checked');
    return Array.from(inputs).map(input => input.value);
  }

  applyProducerFilters() {
    const selectedProducerIds = this.getSelectedProducerIds();
    const section = document.querySelector('results-list, [section-id]');
    if (!section) return;

    const allProductCards = section.querySelectorAll('.product-grid__item');
    const productCardsWithProducer = section.querySelectorAll('.product-grid__item[data-producer-id]');
    let visibleCount = 0;

    if (selectedProducerIds.length === 0) {
      allProductCards.forEach(card => {
        if (!(card instanceof HTMLElement)) return;
        if (card.style.display === 'none') card.style.removeProperty('display');
        if (window.getComputedStyle(card).display !== 'none') visibleCount++;
      });
      this.updateProductCount(visibleCount);
      return;
    }

    productCardsWithProducer.forEach(card => {
      if (!(card instanceof HTMLElement)) return;
      const producerId = card.dataset.producerId;
      if (producerId && selectedProducerIds.includes(producerId)) {
        card.style.display = '';
        visibleCount++;
      } else {
        if (window.getComputedStyle(card).display !== 'none') card.style.display = 'none';
      }
    });

    allProductCards.forEach(card => {
      if (!(card instanceof HTMLElement)) return;
      if (!card.hasAttribute('data-producer-id')) {
        if (window.getComputedStyle(card).display !== 'none') card.style.display = 'none';
      }
    });

    this.updateProductCount(visibleCount);
  }

  updateProducerFilterStatus() {
    const selectedProducerIds = this.getSelectedProducerIds();
    const statusElements = document.querySelectorAll('[id^="producer-filter-status-"], facet-inputs-component[data-filter-type="producer"] facet-status-component span[ref="facetStatus"]');
    statusElements.forEach(statusElement => {
      if (!(statusElement instanceof HTMLElement)) return;
      if (selectedProducerIds.length === 0) {
        statusElement.innerHTML = '';
        statusElement.classList.remove('bubble', 'facets__bubble');
        return;
      }
      const producerNames = selectedProducerIds.map(producerId => {
        const input = document.querySelector(`input[name="filter.producer_id"][value="${producerId}"]`);
        const label = input?.closest('li')?.querySelector('.checkbox__label-text');
        return label?.textContent?.trim() || '';
      }).filter(name => name);
      if (selectedProducerIds.length === 1 && producerNames.length === 1) {
        statusElement.innerHTML = producerNames[0];
        statusElement.classList.remove('bubble', 'facets__bubble');
      } else {
        statusElement.innerHTML = selectedProducerIds.length.toString();
        statusElement.classList.add('bubble', 'facets__bubble');
      }
    });
  }

  updateProductCount(count) {
    updateFacetClientFilteredProductCount(count);
  }
}

/**
 * Handles size (wine bottle format) filtering for products based on data-sizes attribute
 * data-sizes contains pipe-separated size keys (e.g. "75|150|37.5")
 */
class SizeFilterHandler {
  constructor() {
    this.observer = null;
    this.init();
  }

  init() {
    document.addEventListener('sizeFilterUpdate', this.handleSizeFilterUpdate.bind(this));
    window.addEventListener('popstate', this.handleURLChange.bind(this));
    this.setupMutationObserver();
    requestAnimationFrame(() => {
      this.syncSizeFiltersWithURL();
      this.applySizeFilters();
      this.updateSizeFilterStatus();
    });
  }

  syncSizeFiltersWithURL() {
    const url = new URL(window.location.href);
    const sizeParams = url.searchParams.getAll('filter.size');
    const allSizeInputs = document.querySelectorAll('input[name="filter.size"]');
    allSizeInputs.forEach((input) => {
      if (input instanceof HTMLInputElement) input.checked = false;
    });
    if (sizeParams.length > 0) {
      sizeParams.forEach((size) => {
        const input = document.querySelector(`input[name="filter.size"][value="${size}"]`);
        if (input instanceof HTMLInputElement) input.checked = true;
      });
    }
  }

  handleURLChange() {
    this.syncSizeFiltersWithURL();
    this.applySizeFilters();
    this.updateSizeFilterStatus();
  }

  setupMutationObserver() {
    const section = document.querySelector('results-list, [section-id]');
    if (!section) return;
    this.observer = new MutationObserver((mutations) => {
      let shouldReapply = false;
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node instanceof HTMLElement && (node.classList.contains('product-grid__item') || node.querySelector('.product-grid__item'))) {
            shouldReapply = true;
          }
        });
      });
      if (shouldReapply) {
        setTimeout(() => {
          this.syncSizeFiltersWithURL();
          this.applySizeFilters();
          this.updateSizeFilterStatus();
        }, 50);
      }
    });
    this.observer.observe(section, { childList: true, subtree: true });
  }

  handleSizeFilterUpdate() {
    this.applySizeFilters();
    this.updateSizeFilterStatus();
  }

  getSelectedSizes() {
    const inputs = document.querySelectorAll('input[name="filter.size"]:checked');
    return Array.from(inputs).map((input) => input.value);
  }

  applySizeFilters() {
    const selectedSizes = this.getSelectedSizes();
    const section = document.querySelector('results-list, [section-id]');
    if (!section) return;

    const allProductCards = section.querySelectorAll('.product-grid__item');
    const productCardsWithSizes = section.querySelectorAll('.product-grid__item[data-sizes]');
    let visibleCount = 0;

    if (selectedSizes.length === 0) {
      allProductCards.forEach((card) => {
        if (!(card instanceof HTMLElement)) return;
        if (card.style.display === 'none') card.style.removeProperty('display');
        if (window.getComputedStyle(card).display !== 'none') visibleCount++;
      });
      this.updateProductCount(visibleCount);
      return;
    }

    productCardsWithSizes.forEach((card) => {
      if (!(card instanceof HTMLElement)) return;
      const sizesStr = card.dataset.sizes ? String(card.dataset.sizes).trim() : '';
      const cardSizes = sizesStr ? sizesStr.split('|').map((s) => s.trim()).filter(Boolean) : [];
      const hasMatch = selectedSizes.some((sel) => cardSizes.includes(sel));
      if (hasMatch) {
        card.style.display = '';
        visibleCount++;
      } else {
        if (window.getComputedStyle(card).display !== 'none') card.style.display = 'none';
      }
    });

    allProductCards.forEach((card) => {
      if (!(card instanceof HTMLElement)) return;
      if (!card.hasAttribute('data-sizes')) {
        if (window.getComputedStyle(card).display !== 'none') card.style.display = 'none';
      }
    });

    this.updateProductCount(visibleCount);
  }

  updateSizeFilterStatus() {
    const selectedSizes = this.getSelectedSizes();
    const statusElements = document.querySelectorAll('[id^="size-filter-status-"], facet-inputs-component[data-filter-type="size"] facet-status-component span[ref="facetStatus"]');
    statusElements.forEach((statusElement) => {
      if (!(statusElement instanceof HTMLElement)) return;
      if (selectedSizes.length === 0) {
        statusElement.innerHTML = '';
        statusElement.classList.remove('bubble', 'facets__bubble');
        return;
      }
      if (selectedSizes.length === 1) {
        const useDisplayNames = document.body.getAttribute('data-size-display-names') === 'true';
        const sizeLabels = {
          '18.75': 'Split Bottle',
          '37.5': 'Half Bottle',
          '50': '50cl Bottle',
          '75': 'Bottle',
          '150': 'Magnum',
          '300': 'Double Magnum',
          '450': 'Jeroboam',
          '600': 'Imperial',
          '900': 'Salmanazar',
          '1200': 'Balthazar',
          '1500': 'Nebuchadnezzar'
        };
        statusElement.innerHTML = useDisplayNames
          ? (sizeLabels[selectedSizes[0]] || `${selectedSizes[0]}cl Bottle`)
          : `${selectedSizes[0]}cl`;
        statusElement.classList.remove('bubble', 'facets__bubble');
      } else {
        statusElement.innerHTML = selectedSizes.length.toString();
        statusElement.classList.add('bubble', 'facets__bubble');
      }
    });
  }

  updateProductCount(count) {
    updateFacetClientFilteredProductCount(count);
  }
}

/**
 * Syncs region-sd filter checkboxes from URL (filter.p.m.custom.region_sub_last_parent_id).
 * Called after section re-render for region-sd filter.
 */
function syncRegionSDCheckboxesFromUrl() {
  const paramName = 'filter.p.m.custom.region_sub_last_parent_id';
  const url = new URL(window.location.href);
  const selectedIds = url.searchParams.getAll(paramName);

  document.querySelectorAll(`input[name="${paramName}"]`).forEach((input) => {
    if (input instanceof HTMLInputElement) {
      input.checked = selectedIds.includes(input.value);
    }
  });

  const statusElements = document.querySelectorAll(
    '[id^="top-level-regions-filter-status-"], facet-inputs-component[data-filter-type="region-sd"] facet-status-component span[ref="facetStatus"]'
  );
  const regionNames = window.__regionIdToName || {};
  statusElements.forEach((el) => {
    if (!(el instanceof HTMLElement)) return;
    if (selectedIds.length === 0) {
      el.innerHTML = '';
      el.classList.remove('bubble', 'facets__bubble');
      return;
    }
    const regionInput = document.querySelector(`input[name="${paramName}"][value="${selectedIds[0]}"]`);
    const label = regionInput?.getAttribute('data-label') || regionNames[selectedIds[0]] || '';
    if (selectedIds.length === 1 && label) {
      el.innerHTML = label;
      el.classList.remove('bubble', 'facets__bubble');
    } else {
      el.innerHTML = selectedIds.length.toString();
      el.classList.add('bubble', 'facets__bubble');
    }
  });

  if (typeof window.updateRegionFilterPillsAndStatus === 'function') {
    window.updateRegionFilterPillsAndStatus();
  }
}

/**
 * Syncs vintage filter checkboxes from URL (filter.p.m.custom.vintage).
 * Runs on page load, popstate, and after section re-renders (MutationObserver).
 * Needed because Section Rendering API may not pass query params to Liquid.
 */
function syncVintageCheckboxesFromUrl() {
  const url = new URL(window.location.href);
  const selectedYears = url.searchParams.getAll('filter.p.m.custom.vintage');

  document.querySelectorAll('input[name="filter.p.m.custom.vintage"]').forEach((input) => {
    if (input instanceof HTMLInputElement) {
      input.checked = selectedYears.includes(input.value);
    }
  });

  const statusElements = document.querySelectorAll(
    '[id^="vintage-filter-status-"], facet-inputs-component[data-filter-type="vintage"] facet-status-component span[ref="facetStatus"]'
  );
  statusElements.forEach((el) => {
    if (!(el instanceof HTMLElement)) return;
    if (selectedYears.length === 0) {
      el.innerHTML = '';
      el.classList.remove('bubble', 'facets__bubble');
      return;
    }
    if (selectedYears.length === 1) {
      el.innerHTML = selectedYears[0] ?? '';
      el.classList.remove('bubble', 'facets__bubble');
    } else {
      el.innerHTML = selectedYears.length.toString();
      el.classList.add('bubble', 'facets__bubble');
    }
  });
}

class VintageFilterUrlSync {
  constructor() {
    this.sync = this.sync.bind(this);
    this.#observe();
    this.sync();
    window.addEventListener('popstate', this.sync);
    window.addEventListener('section-rendered', this.sync);
  }

  sync() {
    requestAnimationFrame(() => syncVintageCheckboxesFromUrl());
  }

  #observe() {
    const observer = new MutationObserver(() => {
      this.sync();
    });
    const init = () => {
      const resultsList = document.querySelector('results-list');
      const section = resultsList?.closest('[id^="shopify-section-"]') ?? document.querySelector('[id^="shopify-section-"]');
      if (section) {
        observer.observe(section, { childList: true, subtree: true });
      } else {
        requestAnimationFrame(init);
      }
    };
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', init);
    } else {
      init();
    }
  }
}

class RegionFilterUrlSync {
  constructor() {
    this.sync = this.sync.bind(this);
    this.sync();
    window.addEventListener('popstate', this.sync);
    window.addEventListener('section-rendered', this.sync);
  }

  sync() {
    requestAnimationFrame(() => {
      if (typeof window.repopulateRegionFilters === 'function') {
        window.repopulateRegionFilters().then?.(
          () => typeof window.updateRegionFilterPillsAndStatus === 'function' && window.updateRegionFilterPillsAndStatus()
        );
      }
      syncRegionSDCheckboxesFromUrl();
    });
  }
}

/**
 * Restore saved filter params from localStorage (localhost only) when current URL has no filters.
 * Replaces URL and re-renders the facets section so filters and results match.
 */
function restoreSavedFacetsIfNeeded() {
  const saved = getSavedFacetsForRestore();
  if (!saved) return;
  const pathname = window.location.pathname;
  const fullUrl = new URL(pathname + (saved ? '?' + saved : ''), window.location.origin).toString();
  history.replaceState({ urlParameters: saved }, '', fullUrl);
  const form = document.querySelector('facets-form-component');
  const sectionId = form?.getAttribute('section-id');
  if (sectionId) {
    sectionRenderer.renderSection(sectionId, { url: fullUrl, cache: false }).then(() => {
      syncRegionSDCheckboxesFromUrl();
      if (typeof window.repopulateRegionFilters === 'function') {
        window.repopulateRegionFilters().then?.(
          () => typeof window.updateRegionFilterPillsAndStatus === 'function' && window.updateRegionFilterPillsAndStatus()
        );
      }
    });
  }
}

// Initialize filter handlers when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new RegionFilterHandler();
    new SizeFilterHandler();
    new VintageFilterUrlSync();
    new RegionFilterUrlSync();
    restoreSavedFacetsIfNeeded();
  });
} else {
  new RegionFilterHandler();
  new SizeFilterHandler();
  new VintageFilterUrlSync();
  new RegionFilterUrlSync();
  restoreSavedFacetsIfNeeded();
}
